<template>
  <div class="dashboard">
    <section class="admin-panel dashboard-welcome">
      <div class="welcome-copy">
        <p class="eyebrow">欢迎回来</p>
        <h2>{{ welcomeName }}</h2>
        <p>{{ welcomeText }}</p>
      </div>
      <div class="welcome-actions">
        <el-button type="primary" @click="goPublish">发布文章</el-button>
        <el-button @click="goSkillPublish">发布 Skill</el-button>
        <el-button @click="goProfile">个人信息</el-button>
      </div>
    </section>

    <section class="stats-grid">
      <article class="stat-card" v-for="item in stats" :key="item.label">
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
        <small>{{ item.hint }}</small>
      </article>
    </section>

    <section class="admin-content-grid">
      <article class="admin-panel" id="posts">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">文章管理</p>
            <h2>最近文章</h2>
          </div>
          <el-button size="mini" @click="goPosts">查看全部</el-button>
        </div>
        <ul class="post-table">
          <li v-for="post in recentPosts" :key="post.id || post.title">
            <span>{{ post.title }}</span>
            <em>{{ post.updatedAt }}</em>
          </li>
        </ul>
        <div v-if="!loading && recentPosts.length === 0" class="empty-state">
          暂无文章
        </div>
      </article>

      <article class="admin-panel" id="skills">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">SKILL管理</p>
            <h2>最近 Skill</h2>
          </div>
          <el-button size="mini" @click="goSkills">查看全部</el-button>
        </div>
        <ul class="post-table">
          <li v-for="skill in recentSkills" :key="skill.id || skill.name">
            <span>{{ skill.name }}</span>
            <em>{{ skill.updatedAt }}</em>
          </li>
        </ul>
        <div v-if="!loading && recentSkills.length === 0" class="empty-state">
          暂无 Skill
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import { fetchAdminPosts } from '@/services/posts';
import { fetchAdminCategories } from '@/services/categories';
import { fetchAdminSkills } from '@/services/skills';
import { getAdminProfile } from '@/services/auth';
import { trackFrontendEvent } from '@/services/operationLogs';

export default {
  name: 'AdminDashboard',
  data() {
    return {
      loading: false,
      total: 0,
      categoryTotal: 0,
      skillTotal: 0,
      recentPosts: [],
      recentSkills: [],
      profile: {},
    };
  },
  computed: {
    welcomeName() {
      return this.profile.nickname || this.profile.username || '管理员';
    },
    welcomeText() {
      if (this.profile.nickname) {
        return '今天也可以从一篇新文章开始，把想法稳稳放进内容库。';
      }

      return '建议先完善昵称，这样发布文章时会自动使用你的作者名。';
    },
    stats() {
      return [
        {
          label: '文章总数',
          value: String(this.total),
          hint: this.loading ? '读取中' : '已发布内容',
        },
        {
          label: '分类总数',
          value: String(this.categoryTotal),
          hint: this.loading ? '读取中' : '内容分类',
        },
        {
          label: 'Skill 总数',
          value: String(this.skillTotal),
          hint: this.loading ? '读取中' : '可下载能力',
        },
      ];
    },
  },
  created() {
    this.loadDashboard();
  },
  methods: {
    trackAction(operation, detail) {
      trackFrontendEvent({
        operation,
        pagePath: this.$route.path,
        detail,
        success: true,
      });
    },
    goPublish() {
      this.trackAction('点击发布文章入口', '用户在仪表盘点击发布文章按钮');
      this.$router.push('/admin/articles/new');
    },
    goSkillPublish() {
      this.trackAction('点击发布 Skill 入口', '用户在仪表盘点击发布 Skill 按钮');
      this.$router.push('/admin/skills/publish');
    },
    goProfile() {
      this.trackAction('点击个人信息入口', '用户在仪表盘点击个人信息按钮');
      this.$router.push('/admin/profile');
    },
    goPosts() {
      this.trackAction('点击查看全部文章', '用户在仪表盘最近文章模块点击查看全部');
      this.$router.push('/admin/articles');
    },
    goSkills() {
      this.trackAction('点击查看全部 Skill', '用户在仪表盘最近 Skill 模块点击查看全部');
      this.$router.push('/admin/skills');
    },
    normalizePost(item) {
      return {
        id: item.id || item.postId,
        title: item.title || '',
        updatedAt: item.publishedAt || item.updatedAt || item.updateTime || item.createdAt || '-',
      };
    },
    extractPosts(body) {
      const list = Array.isArray(body)
        ? body
        : body?.data?.records || body?.data?.list || body?.data || body?.records || body?.list;

      return Array.isArray(list) ? list.map(this.normalizePost) : [];
    },
    normalizeSkill(item) {
      return {
        id: item.id,
        name: item.name || '',
        updatedAt: item.updatedAt || item.createdAt || '-',
      };
    },
    extractSkills(body) {
      const list = Array.isArray(body)
        ? body
        : body?.data?.records || body?.data?.list || body?.data || body?.records || body?.list;

      return Array.isArray(list) ? list.map(this.normalizeSkill) : [];
    },
    extractTotal(body, fallback) {
      return body?.data?.total || body?.total || body?.data?.count || body?.count || fallback;
    },
    async loadDashboard() {
      this.loading = true;

      try {
        const [postResponse, catResponse, skillResponse, profile] = await Promise.all([
          fetchAdminPosts({ page: 1, size: 5 }),
          fetchAdminCategories().catch(() => ({ data: [] })),
          fetchAdminSkills({ page: 1, size: 5 }).catch(() => ({ data: { data: { records: [], total: 0 } } })),
          getAdminProfile().catch(() => ({})),
        ]);
        const response = postResponse;
        const posts = this.extractPosts(response.data);
        const skills = this.extractSkills(skillResponse.data);

        this.profile = profile || {};
        this.recentPosts = posts;
        this.recentSkills = skills;
        this.total = this.extractTotal(response.data, posts.length);
        this.skillTotal = this.extractTotal(skillResponse.data, skills.length);
        this.categoryTotal = Array.isArray(catResponse?.data?.data || catResponse?.data)
          ? (catResponse?.data?.data || catResponse?.data).length
          : 0;
      } catch (error) {
        this.recentPosts = [];
        this.recentSkills = [];
        this.total = 0;
        this.skillTotal = 0;
        this.$message.error(error.response?.data?.message || '暂时无法读取文章数据');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
